import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import { Tab, Box, Typography } from '@mui/material';

import React, { useState } from 'react';

import { FREQUENCYTYPE } from '../api/type-def';
import styles from '../JobScheduler.module.scss';

import DailyComp from './DailyComp';
import HourlyComp from './HourlyComp';
import MinuteComp from './MinuteComp';
import MonthlyComp from './MonthlyComp';
import WeeklyComp from './WeeklyComp';
import YearlyComp from './YearlyComp';

const { dividerColor } = styles;

interface IScheduleProps {
	handleChange: (val: any) => void;
}

interface ITabPanelProps {
	value: string;
	children: React.ReactNode;
}

const TabComponent = (label: string, value: string, index: number) => (
	<Tab
		key={index}
		label={label}
		value={value}
		className={styles['schedule-tab']}
	/>
);

const TabPanelComponent: React.FC<ITabPanelProps> = ({ children, value }) => (
	<TabPanel value={value} className={styles['tab-panel']}>
		{children}
	</TabPanel>
);

const schedulerTypes = [
	{ label: 'Minutes', value: 'minutes' },
	{ label: 'Hourly', value: 'hourly' },
	{ label: 'Daily', value: 'daily' },
	{ label: 'Weekly', value: 'weekly' },
	{ label: 'Monthly', value: 'monthly' },
	{ label: 'Yearly', value: 'yearly' },
];

const { MINUTES, HOURLY, WEEKLY, MONTHLY, YEARLY } = FREQUENCYTYPE;

const ScheduleComp = (props: IScheduleProps) => {
	const { handleChange } = props;
	const [frequencyType, setFrequencyType] = React.useState('daily');
	const [startsAtSeconds] = useState<number>(0);

	const handleScheduleChange = (val: any) => {
		let obj = {};
		switch (frequencyType) {
			case MINUTES: {
				obj = {
					frequencyType,
					frequency: val,
				};
				break;
			}
			case HOURLY: {
				obj = {
					frequencyType,
					...val,
				};
				break;
			}
			case WEEKLY: {
				const { days, startsAtHour, startsAtMinutes } = val;
				obj = {
					frequencyType,
					days,
					startsAtHour,
					startsAtMinutes,
					startsAtSeconds,
				};
				break;
			}
			case MONTHLY:
			case YEARLY: {
				obj = {
					frequencyType,
					startsAtSeconds,
					...val,
				};
				break;
			}
			default: {
				// case FREQUENCYTYPE.DAILY will be handled here
				const { skipWeekend, startsAtHour, startsAtMinutes } = val;
				obj = {
					frequencyType,
					skipWeekend,
					startsAtHour,
					startsAtMinutes,
					startsAtSeconds,
				};
				break;
			}
		}
		handleChange(obj);
	};

	const handleTabs = (event: React.SyntheticEvent, newValue: string) => {
		setFrequencyType(newValue);
	};

	return (
		<Box sx={{ width: '100%', typography: 'body1' }}>
			<Typography className={styles['font-weight-bold']}>
				Schedule / Cron Expression
			</Typography>
			<TabContext value={frequencyType}>
				<Box
					sx={{
						borderBottom: 1,
						borderColor: dividerColor,
						width: '60%',
					}}
				>
					<TabList
						onChange={handleTabs}
						aria-label="lab API tabs example"
						indicatorColor="primary"
					>
						{schedulerTypes.map((schedule, index) =>
							TabComponent(schedule.label, schedule.value, index),
						)}
					</TabList>
				</Box>
				<TabPanelComponent value="minutes">
					<MinuteComp handleChange={handleScheduleChange} />
				</TabPanelComponent>
				<TabPanelComponent value="hourly">
					<HourlyComp handleChange={handleScheduleChange} />
				</TabPanelComponent>
				<TabPanelComponent value="daily">
					<DailyComp handleChange={handleScheduleChange} />
				</TabPanelComponent>
				<TabPanelComponent value="weekly">
					<WeeklyComp handleChange={handleScheduleChange} />
				</TabPanelComponent>
				<TabPanelComponent value="monthly">
					<MonthlyComp handleChange={handleScheduleChange} />
				</TabPanelComponent>
				<TabPanelComponent value="yearly">
					<YearlyComp handleChange={handleScheduleChange} />
				</TabPanelComponent>
			</TabContext>
		</Box>
	);
};

export default ScheduleComp;
